import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Pencil, ShieldOff, ShieldCheck, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = {
  admin: "Admin",
  qa_manager: "QA Manager",
  site_engineer: "Site Engineer",
  client: "Client Reviewer",
  subcontractor: "Subcontractor",
};

const ROLE_BADGE = {
  admin: "border-rose-200 bg-rose-50 text-rose-700",
  qa_manager: "border-teal-200 bg-teal-50 text-teal-700",
  site_engineer: "border-sky-200 bg-sky-50 text-sky-700",
  client: "border-emerald-200 bg-emerald-50 text-emerald-700",
  subcontractor: "border-amber-200 bg-amber-50 text-amber-700",
};

const blankForm = { name: "", email: "", role: "site_engineer", company: "", password: "", project_ids: [] };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, otherwise user object
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  const isAdmin = me?.role === "admin";
  const isQA = me?.role === "qa_manager";
  const allowedCreateRoles = isAdmin
    ? ["admin", "qa_manager", "site_engineer", "client", "subcontractor"]
    : ["site_engineer", "client"];

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/users"), api.get("/projects")])
      .then(([u, p]) => { setUsers(u.data || []); setProjects(p.data || []); })
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ ...blankForm, role: allowedCreateRoles[0] }); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", email: u.email || "", role: u.role, company: u.company || "", password: "", project_ids: u.project_ids || [] });
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) {
        const body = { name: form.name, role: form.role, company: form.company, project_ids: form.project_ids };
        await api.patch(`/users/${editing.id}`, body);
        toast.success("User updated");
      } else {
        const body = { name: form.name, email: form.email, role: form.role, company: form.company, project_ids: form.project_ids };
        if (form.password) body.password = form.password;
        await api.post("/users", body);
        toast.success("User added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      const path = u.is_active === false ? "activate" : "deactivate";
      await api.post(`/users/${u.id}/${path}`);
      toast.success(`User ${path}d`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  const canEditUser = (u) => {
    if (isAdmin) return true;
    if (!isQA) return false;
    // QA Manager: cannot edit admin / other qa_manager.
    return !["admin", "qa_manager"].includes(u.role);
  };

  return (
    <div className="p-4 md:p-8" data-testid="users-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="overline text-teal-700">User management</div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">Users</h1>
          <p className="mt-1 text-sm text-slate-600">
            {isAdmin
              ? "Add and manage all users in this workspace."
              : "Add Site Engineers and Client Reviewers for your projects."}
          </p>
        </div>
        <Button data-testid="add-user-btn" onClick={openCreate} className="bg-teal-700 hover:bg-teal-800">
          <UserPlus className="mr-2 size-4" /> Add user
        </Button>
      </div>

      <Card className="mt-6 overflow-hidden border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Projects</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>)}
              {!loading && users.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500"><UsersIcon className="mx-auto mb-2 size-8 text-slate-300" />No users yet.</td></tr>
              )}
              {users.map((u) => {
                const active = u.is_active !== false;
                return (
                  <tr key={u.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{u.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{u.email}</td>
                    <td className="px-4 py-3"><Badge variant="outline" className={ROLE_BADGE[u.role] || ""} data-testid={`user-role-${u.id}`}>{ROLE_LABEL[u.role] || u.role}</Badge></td>
                    <td className="px-4 py-3 text-slate-700">{u.company || <span className="text-slate-400">—</span>}</td>
                    <td className="px-4 py-3 text-slate-700">{(() => {
                      const n = (u.project_ids || []).length;
                      const workspaceWide = ["admin", "qa_manager", "platform_admin"].includes(u.role);
                      if (n > 0) return n;
                      return workspaceWide
                        ? <span className="text-slate-400">All</span>
                        : <span className="text-rose-600 font-medium">None</span>;
                    })()}</td>
                    <td className="px-4 py-3">
                      {active
                        ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">Active</Badge>
                        : <Badge variant="outline" className="border-slate-300 bg-slate-100 text-slate-600" data-testid={`user-inactive-${u.id}`}>Inactive</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      {canEditUser(u) && (
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="mr-1 size-3.5" />Edit</Button>
                          {u.id !== me?.id && (
                            <Button size="sm" variant="ghost" onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}>
                              {active ? <><ShieldOff className="mr-1 size-3.5" />Deactivate</> : <><ShieldCheck className="mr-1 size-3.5" />Activate</>}
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit user" : "Add new user"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update role, project access or status." : "Send the user their email and password to sign in."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full name</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="user-email-input" type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(editing
                    ? (isAdmin
                        ? ["admin", "qa_manager", "site_engineer", "client", "subcontractor"]
                        : ["site_engineer", "client"])
                    : allowedCreateRoles
                  ).map((r) => (
                    <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isQA && (
                <p className="mt-1 text-xs text-slate-500">QA Managers can only assign Site Engineer or Client Reviewer roles.</p>
              )}
            </div>
            <div>
              <Label>Company</Label>
              <Input data-testid="user-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div>
              <Label>Project access</Label>
              <p className="text-xs text-slate-500 mb-1">
                <strong>Admins &amp; QA Managers</strong> always see every project in the workspace, regardless of this list.<br />
                <strong>Site Engineers, Client Reviewers &amp; Subcontractors</strong> see <em>only</em> the projects ticked below. Leaving it empty means <span className="text-rose-700 font-semibold">no project access</span>.
              </p>
              <div className="max-h-32 overflow-y-auto rounded-md border border-slate-200 p-2">
                {projects.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 py-1 text-sm">
                    <input
                      type="checkbox"
                      data-testid={`project-access-${p.id}`}
                      checked={(form.project_ids || []).includes(p.id)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...(form.project_ids || []), p.id]
                          : (form.project_ids || []).filter((id) => id !== p.id);
                        setForm({ ...form, project_ids: next });
                      }}
                    />
                    <span>{p.name}</span>
                    {p.number && <span className="text-xs font-mono text-slate-500">{p.number}</span>}
                  </label>
                ))}
                {projects.length === 0 && <p className="text-xs text-slate-500">No projects yet.</p>}
              </div>
            </div>
            {!editing && (
              <div>
                <Label>Initial password (optional)</Label>
                <Input data-testid="user-password-input" type="text" placeholder="Leave blank to use the default demo password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving || !form.name || (!editing && !form.email)} className="bg-teal-700 hover:bg-teal-800" data-testid="save-user-btn">
              {saving ? "Saving…" : editing ? "Save changes" : "Add user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
